import { createTheme } from "@mui/material";
import { TenantAssets, Assets as DefaultAssets } from "../assets/assets";
import { TenantConfig as TConfig } from "../types/tenantConfig";
import { getTenantName, getTenantSlugFromPath, isSharedDeployment } from "./index";
import { DefaultConfig } from "./defaultConfig";
import { theme as fallbackTheme } from "../material-ui/theme";
import kolulaDefaultLogo from "../tenants/kolula/assets/logo/logo.webp";
import kolulaDefaultFavicon from "../tenants/kolula/assets/favicon/kolula.webp";

// --- Shared-deployment runtime config (API-driven) -----------------------
//
// On the shared deployment (REACT_APP_TENANT unset, tenant resolved from the
// URL slug) the per-tenant config/branding is NOT bundled in src/tenants/. It is
// fetched at runtime from the public endpoint GET /app/tenant-config/:slug and
// applied to the theme / CSS variables / favicon / SEO. Legacy (pinned) and dev
// builds keep using the build-time folder imports unchanged.

// Mirror of new-backend/app/src/lib/tenantConfig.ts `PublicTenantConfig` — the
// non-sensitive subset of the config the end-user app needs (no api keys).
// A home-carousel banner as the public config endpoint now returns it: an
// object carrying a per-language `images` map plus optional deep-link / voucher.
// The backend has already filtered (active + in-window) and sorted (by `order`)
// the list, so the end-user app renders it verbatim. Legacy stored data is still
// tolerated for back-compat: a bare string OR an object with a single `src`
// string is treated as one image shown for every language.
export type RuntimeBanner =
  | string
  | {
      images?: { [lang: string]: string };
      /** Legacy single image — shown for all languages. */
      src?: string;
      href?: string;
      voucherCode?: string;
      type?: "explainer" | "promo" | "notice";
    };

interface PublicBranding {
  primaryColor?: string; // CSS-ready RGB triplet, e.g. "0, 84, 117"
  onPrimaryColor?: string;
  logoUrl?: string;
  faviconUrl?: string;
  banners?: RuntimeBanner[]; // home-carousel banners, owner-uploaded
}
export interface PublicTenantConfigDTO {
  slug: string;
  organizationName: string;
  solution: string;
  services: string[];
  categories?: {
    key: string;
    useCase: string;
    name: string;
    langNames: Record<string, { title?: string }> | null;
    /** ADR-0026 §A; absent = not established, read as `true`. */
    hasBookableProducts?: boolean;
    paymentTiming?: NonNullable<TConfig["categories"]>[number]["paymentTiming"];
    /**
     * ADR-0026 §B. Declared because it IS served and IS carried through the
     * mapping below — it was simply missing from this type, which made the
     * DTO read as if the app never received a category's configuration.
     */
    config?: NonNullable<TConfig["categories"]>[number]["config"];
  }[];
  branding: PublicBranding;
  seo: TConfig["seo"];
  contact: { email: string | null; phone: string | null; website: string | null };
  legalNotice?: {
    street: string | null;
    postalCode: string | null;
    city: string | null;
    managingDirector: string | null;
    hrb: string | null;
    hrbCourt: string | null;
    vatId: string | null;
  };
  offerValidityDays?: number | null;
  /** Resolved server-side, so it is a number whenever the DTO loads (#2352). */
  paymentTermDays?: number;
  legalTexts?: {
    paymentTermsDe?: string | null;
    paymentTermsEn?: string | null;
    noWithdrawalDe?: string | null;
    noWithdrawalEn?: string | null;
    agbNoteDe?: string | null;
    agbNoteEn?: string | null;
  };
  languages: string[];
  fallbackLng: string;
  currency: string;
  currencySymbol: string;
  paymentProvider: string[];
  toolbarItems: TConfig["toolbarItems"];
  allowCoupons: boolean;
  allowNewsLetter: boolean;
  consentRequired: boolean;
  authentication: "none" | "entraid";
  accountsEnabled?: boolean;
  skipPayment: boolean;
  /** §19 UStG small business (#2959). */
  isKleinunternehmer?: boolean;
  /** Who the user's counterparty is — resolved by the backend from the
   *  tenant's own legal identity. */
  terms?: TConfig["terms"];
  /** The tenant's own terms documents in force; empty for kolula's default. */
  ownTerms?: TConfig["ownTerms"];
  /** The tenant's own FAQ questions (always served by current backends). */
  faqs?: TConfig["faqs"];
  /** Per-use-case payment timing, resolved by the backend. */
  paymentTiming?: TConfig["paymentTiming"];
  /** The data protection officer for the privacy policy; null = the tenant. */
  privacyOfficer?: TConfig["privacyOfficer"];
  /** Per-use-case charging mode from Services; absent use cases are paid. */
  charging?: TConfig["charging"];
  /** Generic check-in/check-out flow config. Absent until the tenant configures
   *  it in the admin self-config editor — then this drives the end-user flow. */
  checkInCheckOutFlow?: TConfig["checkInCheckOutFlow"];
  /** Wire 2: per-use-case booking flow (true = direct booking, false =
   *  reservation request). Absent per use case = keep the legacy flow. */
  directBooking?: TConfig["directBooking"];
  /** Per-use-case booking window (lead buffer, horizon, follow-up buffer),
   *  passed through from the stored config. Absent per use case = the
   *  documented defaults in `useBookingWindow`, which reproduce exactly what
   *  the screens hardcoded before the setting existed. */
  bookingWindow?: TConfig["bookingWindow"];
  /** Check-in / check-out hours of a stay. Absent = the documented fallback
   *  in `stayTimes.ts`. */
  stayTimes?: TConfig["stayTimes"];
  /** Per-use-case event types. Absent per use case = the standard list
   *  (`useEventTypeOptions`). */
  eventTypes?: TConfig["eventTypes"];
  /** M10: per-use-case cancellation rule. Absent per use case = no rule, the
   *  AGB governs (`resolveCancellationRule`). */
  cancellation?: TConfig["cancellation"];
  /** ADR-0026 §W: use cases whose standard product category holds a
   *  catalogue. Absent = not established, and the home tiles stay as before. */
  standardCatalogue?: TConfig["standardCatalogue"];
  /** Content of the "more to come" / ideas card. Present ⇔ the backend also
   *  appended `'moreToCome'` to `services`; merged into `content.<lang>` so
   *  the card and page render exactly like a bundled tenant's block. */
  moreToCome?: Partial<Record<"de" | "en", {
    header_text?: string;
    first_text?: string;
    second_text?: string;
    alternative_title?: string;
  }>>;
}

/**
 * True only on the shared deployment serving a URL-resolved tenant. Active when
 * the shared-mode flag is set (explicit, even with a baked-in REACT_APP_TENANT)
 * or no tenant is pinned — and a valid slug is present in the URL.
 */
export const isSharedMode = (): boolean =>
  (isSharedDeployment() || !process.env.REACT_APP_TENANT) &&
  getTenantSlugFromPath() != null;

// Why the URL slug didn't resolve to a live tenant:
//   - "not_found": no such tenant (unknown/typo slug, archived, or no slug at
//     all). The app shows a "wrong link" screen with kolula support contact.
//   - "inactive": the tenant exists but its booking page isn't live yet.
export type TenantNotAvailableReason = "not_found" | "inactive";

// Tenant identity carried with an "inactive" 404, so the not-available screen
// can render a branded "momentarily inactive" page pointing at the tenant's
// own contact. All fields optional — a tenant may not have a logo/contact yet.
export interface TenantNotAvailableInfo {
  organizationName?: string;
  logoUrl?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

let runtimeConfigPromise: Promise<TConfig> | null = null;
let runtimeBranding: PublicBranding | null = null;
let runtimeNotAvailable = false;
let runtimeNotAvailableReason: TenantNotAvailableReason | null = null;
let runtimeNotAvailableTenant: TenantNotAvailableInfo | null = null;

// The shared end-user app loads its config from the admin backend's public
// endpoint. Its base URL is configured per Amplify branch; falls back to the
// app API base if a dedicated var isn't set.
const tenantConfigEndpoint = (slug: string): string => {
  // The public config endpoint lives on the new-backend APP service (the
  // end-user app's backend), whose base (REACT_APP_NEW_API_BASE_URL) already
  // includes the `/app` prefix — same base the customer-agreements calls use.
  const base = (process.env.REACT_APP_NEW_API_BASE_URL ?? "").replace(/\/+$/, "");
  return `${base}/tenant-config/${encodeURIComponent(slug)}`;
};

// Read the `reason` (and, for "inactive", the tenant identity) from a 404
// tenant-config response. Defaults to "not_found" when the body is missing,
// unparseable, or carries an unexpected value — a wrong link is the safer
// default to surface support contact.
const read404Body = async (
  res: Response,
): Promise<{ reason: TenantNotAvailableReason, tenant: TenantNotAvailableInfo | null }> => {
  try {
    const body = (await res.json()) as {
      reason?: unknown;
      tenant?: Partial<TenantNotAvailableInfo>;
    };
    const reason: TenantNotAvailableReason =
      body?.reason === "inactive" ? "inactive" : "not_found";
    const raw = reason === "inactive" && body?.tenant ? body.tenant : null;
    const tenant: TenantNotAvailableInfo | null = raw
      ? {
          organizationName:
            typeof raw.organizationName === "string" ? raw.organizationName : undefined,
          logoUrl: typeof raw.logoUrl === "string" ? raw.logoUrl : null,
          contactEmail: typeof raw.contactEmail === "string" ? raw.contactEmail : null,
          contactPhone: typeof raw.contactPhone === "string" ? raw.contactPhone : null,
        }
      : null;
    return { reason, tenant };
  } catch {
    return { reason: "not_found", tenant: null };
  }
};

/**
 * The served DTO → the app's config. Every field the endpoint serves is either
 * carried here or read off the DTO elsewhere in this file — a field left out
 * is silently dropped for every shared-mode tenant, invisible to types and to a
 * unit test of its consumer. `__tests__/mapPublicConfig.test.ts` fails on the
 * next one.
 */
export const mapPublicConfig = (dto: PublicTenantConfigDTO): TConfig => ({
  ...DefaultConfig,
  organizationName: dto.organizationName,
  legalNotice: dto.legalNotice,
  // #1702: the tenant's own contract wording, so the acceptance screen shows
  // exactly what the acceptance email shows.
  offerValidityDays: dto.offerValidityDays,
  // #2352: the tenant's payment target, so the screen states the same number
  // the acceptance mail states instead of a static sentence.
  paymentTermDays: dto.paymentTermDays,
  legalTexts: dto.legalTexts,
  seo: dto.seo,
  languages: dto.languages,
  fallbackLng: dto.fallbackLng,
  currency: dto.currency,
  currencySymbol: dto.currencySymbol,
  paymentProvider: dto.paymentProvider,
  toolbarItems: dto.toolbarItems,
  allowCoupons: dto.allowCoupons,
  allowNewsLetter: dto.allowNewsLetter,
  consentRequired: dto.consentRequired,
  authentication: dto.authentication,
  // H-3a: runtime-config tenants (the shared deployment) get the account
  // surface from the DTO — bundled config files only cover pinned builds.
  accountsEnabled: dto.accountsEnabled ?? false,
  skipPayment: dto.skipPayment,
  isKleinunternehmer: dto.isKleinunternehmer === true,
  services: dto.services as TConfig["services"],
  // ADR-0026 §A. Absent from an older deploy of the app service, which is why
  // it is optional here and defaulted rather than assumed — the two are
  // separate deploys and the app must not break on the older one.
  categories: dto.categories ?? [],
  // The tenant's own FAQ questions and the resolved payment timing per use
  // case (ADR-0003 amendment 2026-10-06). Absent from an older app-service
  // deploy; every reader treats undefined as "none".
  faqs: dto.faqs,
  paymentTiming: dto.paymentTiming,
  contact: {
    ...DefaultConfig.contact,
    ...(dto.contact.email ? { email: dto.contact.email } : {}),
    ...(dto.contact.phone
      ? { phone: dto.contact.phone, phoneFormatted: dto.contact.phone }
      : {}),
    ...(dto.contact.website ? { website: dto.contact.website } : {}),
  },
  // The top-level `email` the moreToCome page's mailto reads (bundled tenants
  // set it directly, e.g. vonovia's `email: "leihbar@vonovia.de"`). Absent ⇒
  // the DefaultConfig value from the spread above stays.
  ...(dto.contact.email ? { email: dto.contact.email } : {}),
  // Backend-driven flow config wins when the tenant has configured it; absent ⇒
  // the spread DefaultConfig value (off unless REACT_APP_GENERIC_FLOW in dev).
  ...(dto.checkInCheckOutFlow ? { checkInCheckOutFlow: dto.checkInCheckOutFlow } : {}),
  // Wire 2: per-use-case booking flow. Absent ⇒ key stays unset and every
  // use case keeps its hardcoded legacy flow (useDirectBookingFlow).
  ...(dto.directBooking ? { directBooking: dto.directBooking } : {}),
  // Absent ⇒ key stays unset and the T&C page keeps the standing variant
  // (tenant as counterparty, paid).
  ...(dto.terms ? { terms: dto.terms } : {}),
  // Absent or empty ⇒ kolula's default document / the website link applies.
  ...(Array.isArray(dto.ownTerms) && dto.ownTerms.length > 0 ? { ownTerms: dto.ownTerms } : {}),
  // Absent or null ⇒ the privacy policy names the tenant itself.
  ...(dto.privacyOfficer ? { privacyOfficer: dto.privacyOfficer } : {}),
  ...(dto.charging ? { charging: dto.charging } : {}),
  // Absent ⇒ the key stays unset and every use case keeps the documented
  // default window (`useBookingWindow`), which is today's behaviour.
  ...(dto.bookingWindow ? { bookingWindow: dto.bookingWindow } : {}),
  // Absent ⇒ the key stays unset and the fallback hours apply.
  ...(dto.stayTimes ? { stayTimes: dto.stayTimes } : {}),
  // Absent ⇒ the key stays unset and every use case keeps the standard list.
  ...(dto.eventTypes ? { eventTypes: dto.eventTypes } : {}),
  // M10. Absent ⇒ undefined: no rule, the summary shows the AGB sentence.
  // Assigned directly — no bundled default exists for it to keep.
  cancellation: dto.cancellation,
  // ADR-0026 §W. Absent ⇒ unset, and `homeTiles` adds no standard tile.
  ...(dto.standardCatalogue ? { standardCatalogue: dto.standardCatalogue } : {}),
});

const fetchRuntimeConfig = async (): Promise<TConfig> => {
  const slug = getTenantSlugFromPath();
  if (!slug) {
    // Bare domain / no slug segment — no booking page to resolve.
    runtimeNotAvailable = true;
    runtimeNotAvailableReason = "not_found";
    return DefaultConfig;
  }
  try {
    const res = await fetch(tenantConfigEndpoint(slug));
    if (res.status === 404) {
      // Unknown or not-yet-activated tenant — show the not-available page. The
      // body's `reason` distinguishes a wrong link ("not_found") from a tenant
      // whose booking page isn't live yet ("inactive"); default to "not_found".
      // For "inactive" the body also carries the tenant identity for a branded
      // screen.
      const { reason, tenant } = await read404Body(res);
      runtimeNotAvailable = true;
      runtimeNotAvailableReason = reason;
      runtimeNotAvailableTenant = tenant;
      return DefaultConfig;
    }
    if (!res.ok) {
      console.error(`Failed to load tenant config (${res.status})`);
      return DefaultConfig;
    }
    const dto = (await res.json()) as PublicTenantConfigDTO;
    runtimeBranding = dto.branding ?? null;
    const config = mapPublicConfig(dto);
    // Attach the default Infos-tab hint pages (FAQs / T&C / privacy / imprint /
    // accessibility / help). Operational tenants have no bundled config and the
    // runtime DTO can't carry React components, so they come from the default
    // config in `defaults/config/` — its own components, which read THIS
    // tenant's data (organization name, contact, Impressum, use cases) back out
    // of the store; no bundled tenant's content is involved. Lazily imported so the
    // default's config ↔ hint-component circular dependency stays out of the
    // static graph — a static import throws "__WEBPACK_DEFAULT_EXPORT__ before
    // initialization". Only `content` is taken; booking business rules are not.
    try {
      const { Config: defaultCfg } = await import("../defaults/config/config");
      config.content = defaultCfg.content;
    } catch (e) {
      console.error("Failed to load default hints content:", e);
    }
    // Merge the tenant's moreToCome texts INTO the (default) content block —
    // after the default assignment above, which replaces `content` wholesale.
    // The card and page then read `content.<lang>.moreToCome` exactly as they
    // do for bundled tenants. Missing texts become empty strings, which is
    // what the page renders for absent fields anyway.
    if (dto.moreToCome) {
      const content: TConfig["content"] = { ...config.content };
      for (const lang of ["de", "en"] as const) {
        const block = dto.moreToCome[lang];
        if (!block) continue;
        content[lang] = {
          ...content[lang],
          moreToCome: {
            header_text: block.header_text ?? "",
            first_text: block.first_text ?? "",
            second_text: block.second_text ?? "",
            ...(block.alternative_title
              ? { alternative_title: block.alternative_title }
              : {}),
          },
        };
      }
      config.content = content;
    }
    return config;
  } catch (error) {
    // Network/parse error: degrade to default rather than block the whole app.
    console.error("Error loading runtime tenant config:", error);
    return DefaultConfig;
  }
};

const loadRuntimeConfig = (): Promise<TConfig> => {
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = fetchRuntimeConfig();
  }
  return runtimeConfigPromise;
};

/**
 * True when the URL slug resolved to no live tenant (404). Drives the
 * not-available page; only meaningful after the config load has resolved.
 */
export const isTenantNotAvailable = (): boolean => runtimeNotAvailable;

/**
 * Why the tenant isn't available — "not_found" (wrong link → show support
 * contact) vs "inactive" (booking page not live yet). Defaults to "not_found".
 * Only meaningful once `isTenantNotAvailable()` is true.
 */
export const getTenantNotAvailableReason = (): TenantNotAvailableReason =>
  runtimeNotAvailableReason ?? "not_found";

/**
 * Tenant identity for the "inactive" not-available screen (name, logo, the
 * tenant's own contact). Null for the "not_found" case or when the backend
 * sent no tenant block. Only meaningful once `isTenantNotAvailable()` is true.
 */
export const getTenantNotAvailableInfo = (): TenantNotAvailableInfo | null =>
  runtimeNotAvailableTenant;

/**
 * Owner-uploaded home-carousel banners (shared mode). Empty when the tenant
 * hasn't uploaded any. The backend has already filtered + sorted them, so the
 * caller renders them verbatim. Items may be plain strings (legacy data) or
 * objects with `src` + optional `href` / `voucherCode`. Only meaningful after
 * the config load resolves — call sites already await `loadRuntimeConfig()` via
 * `TenantConfig()`. Items carry a per-language `images` map; legacy items may be
 * a bare string or an object with a single `src` (one image for all languages).
 */
export const getRuntimeBanners = (): RuntimeBanner[] => runtimeBranding?.banners ?? [];

// --- On-primary derivation (ADR 0024) ------------------------------------
//
// The contrast color on the accent is NOT configured — it's derived from the
// tenant primary by luminance: white on a dark primary, black on a light one
// (maximizing WCAG contrast). This removes a footgun (a tenant setting a
// grey/translucent on-primary and breaking legibility) and reproduces every
// current tenant exactly. Colors are CSS RGB triplet strings ("r, g, b").

const relativeLuminance = (r: number, g: number, b: number): number => {
  const lin = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

// "174, 11, 4" → "255, 255, 255" (white) for dark primaries, "0, 0, 0" (black)
// for light ones. Threshold 0.179 is the black/white max-contrast crossover.
// Falls back to white for an unparseable primary (brand colors skew dark).
const deriveOnPrimaryTriplet = (primaryTriplet: string): string => {
  const m = primaryTriplet.match(/(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/);
  if (!m) return "255, 255, 255";
  const [r, g, b] = [m[1], m[2], m[3]].map(Number);
  return relativeLuminance(r, g, b) > 0.179 ? "0, 0, 0" : "255, 255, 255";
};

/**
 * Applies the runtime branding colors as CSS variables (shared mode). The legacy
 * path imports the tenant's `variables.css` instead. With no tenant primary set,
 * the `index.css` defaults (kolula turquoise + black on-primary) stay in place.
 * On-primary is derived from the primary, not read from config (ADR 0024).
 */
export const applyRuntimeCssVariables = (): void => {
  const primaryTriplet = runtimeBranding?.primaryColor;
  if (!primaryTriplet) return;
  const root = document.documentElement;
  root.style.setProperty("--color-primary", primaryTriplet);
  root.style.setProperty("--color-onPrimary", deriveOnPrimaryTriplet(primaryTriplet));
};

// --- Config / asset loaders ----------------------------------------------

export const TenantConfig = async (): Promise<TConfig> => {
  if (isSharedMode()) {
    return loadRuntimeConfig();
  }
  const tenantName = getTenantName();
  // Non-pinned build hit without a resolvable tenant — e.g. the bare domain
  // "/" on the shared deployment (no slug segment, no REACT_APP_TENANT, no dev
  // override). There is no tenant to load, so flag not-available and let the
  // caller render TenantNotAvailable instead of crashing in components that
  // require a tenant name (HomePage, Banners, …).
  if (!tenantName) {
    runtimeNotAvailable = true;
    runtimeNotAvailableReason = "not_found";
    return DefaultConfig;
  }
  try {
    const { Config } = await import(`../tenants/${tenantName}/config`);
    return Config;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Tenant config not found for ${tenantName}`);
    return DefaultConfig;
  }
};

export const getLogo = async () => {
  if (isSharedMode()) {
    await loadRuntimeConfig();
    // Try the DB-driven branding first; until tenants can upload their own
    // logo, fall back to the kolula default asset (not the "kolula" string id).
    return runtimeBranding?.logoUrl ?? kolulaDefaultLogo;
  }
  const tenantName = getTenantName();
  try {
    const { Assets }: { Assets: TenantAssets } = await import(
      `../tenants/${tenantName}/assets/assets`
    );
    return Assets.logo;
  } catch (error) {
    return DefaultAssets.logo.kolula;
  }
};

export const loadTheme = async () => {
  if (isSharedMode()) {
    await loadRuntimeConfig();
    // Default the shared deployment to the standalone default theme (a copy of
    // the seezeit look — palette/backgrounds, MUI button/accordion overrides,
    // typography, rounded shape) in src/defaults/theme.
    const { theme: defaultTheme } = await import("../defaults/theme/theme");
    const primaryTriplet = runtimeBranding?.primaryColor;
    // No tenant primary → keep the default theme verbatim (the kolula/seezeit
    // look: turquoise accent + black on-primary). Only tenants that picked a
    // primary get the derived accent theme.
    if (!primaryTriplet) return defaultTheme;

    // ADR 0024: derive the on-primary from the primary (never read from config)
    // and map EVERY accent-dependent palette slot onto primary / on-primary so
    // none keeps the hardcoded turquoise default. Neutral, non-accent slots
    // (background, text, secondary.main, borders, input.border/error,
    // text.disabled, fonts, radius, component overrides) stay on the default.
    const primary = `rgb(${primaryTriplet})`;
    const onPrimary = `rgb(${deriveOnPrimaryTriplet(primaryTriplet)})`;
    const dimmedPrimary = `rgba(${primaryTriplet}, 0.3)`;
    return createTheme(defaultTheme, {
      palette: {
        primary: { main: primary, light: primary, contrastText: onPrimary },
        secondary: { contrastText: primary },
        info: { main: primary },
        // Focus ring goes neutral (not the accent) — matches the bundled
        // tenant look and avoids a coloured ring fighting the primary.
        input: { ...defaultTheme.palette.input, focus: "#333333" },
        action: {
          selected: onPrimary,
          active: onPrimary,
          disabled: dimmedPrimary,
          disabledBackground: dimmedPrimary,
        },
        button: { text: onPrimary },
      },
    });
  }
  const tenantName = getTenantName();
  try {
    const { theme } = await import(`../tenants/${tenantName}/layout/theme`);
    return theme;
  } catch (error) {
    return fallbackTheme; // Fallback theme
  }
};

export const setFavicon = async () => {
  if (isSharedMode()) {
    await loadRuntimeConfig();
    // DB-driven favicon if present, otherwise the kolula default.
    setFaviconHref(runtimeBranding?.faviconUrl ?? kolulaDefaultFavicon);
    return;
  }
  const tenantName = getTenantName();
  try {
    const favicon = await import(`../tenants/${tenantName}/assets/favicon/${tenantName}.webp`);
    setFaviconHref(favicon.default);
  } catch (error) {
    console.error("Error loading tenant favicon:", error);
  }
};

const setFaviconHref = (href: string) => {
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.getElementsByTagName("head")[0].appendChild(link);
  }
  link.href = href;
};

export const setSeoTags = async () => {
  if (isSharedMode()) {
    const config = await loadRuntimeConfig();
    applySeo(config.seo);
    return;
  }
  const tenantName = getTenantName();
  try {
    const { Config: config } = await import(`../tenants/${tenantName}/config`);
    applySeo(config.seo);
  } catch (e) {
    applySeo(DefaultConfig.seo);
    console.error("Error loading tenant seo config:", e);
  }
};

const applySeo = (seo: TConfig["seo"]) => {
  document.title = seo.appTitle;
  setMetaTag("description", seo.appDescription);

  //Set Open Graph tags for SEO
  setOgTag("title", seo.ogTitle);
  setOgTag("description", seo.ogDescription);
  setOgTag("url", seo.ogUrl);
  setOgTag("image", seo.ogImage);
};

const setMetaTag = (name: string, content: string) => {
  let metaTag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (metaTag) {
    metaTag.setAttribute("content", content);
  } else {
    metaTag = document.createElement("meta") as HTMLMetaElement;
    metaTag.name = name;
    metaTag.content = content;
    document.head.appendChild(metaTag);
  }
};

const setOgTag = (property: string, content: string) => {
  let ogTag = document.querySelector(`meta[property="og:${property}"]`) as HTMLMetaElement;
  if (ogTag) {
    ogTag.setAttribute("content", content);
  } else {
    ogTag = document.createElement("meta") as HTMLMetaElement;
    ogTag.setAttribute("property", `og:${property}`);
    ogTag.content = content;
    document.head.appendChild(ogTag);
  }
};

export default TenantConfig;
